/*
 * Copyright 2026 North Carolina State University
 */

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  Paper,
  Typography,
  Grid,
  Button,
  Box,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import SchemaField from './SchemaField';
import { buildInitialFormData, mergeReadonlyPrefill, validateAgainstSchema } from './schemaUtils';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Renders a request form from a server-supplied schema.
 *
 * Modes:
 *   - mode="create"    : POST /api/requests
 *   - mode="edit"      : PUT  /api/requests/:id       (client edit while open)
 *   - mode="admin_edit": PUT  /api/admin/requests/:id (admin edit)
 *
 * There is no resubmit mode. The revision cycle is gone: while a request is
 * open either side simply edits the form, and the save records a field-level
 * diff (what changed, by whom, when) that both sides can read.
 */
const DynamicRequestForm = ({
  schemaKey,
  project,
  request = null,
  mode = 'create',
  onSubmitted,
  onCancel
}) => {
  const { user: currentUser } = useAuth();
  const [schema, setSchema] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [comment, setComment] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const key = schemaKey || request?.request_type_key;
        if (!key) {
          setErrorMsg('No request type provided');
          setLoading(false);
          return;
        }
        const facilityId = project?.facility?._id || request?.facility?._id || request?.facility;
        const url = facilityId
          ? `/api/requests/schemas/${key}?facilityId=${facilityId}`
          : `/api/requests/schemas/${key}`;
        const { data } = await axios.get(url);
        if (cancelled) return;
        setSchema(data);
        if (request?.form_data) {
          // Existing request: show what the client submitted, and back-fill any
          // empty readonly identity fields (older requests, edge cases) without
          // overwriting client-entered values.
          setFormData(mergeReadonlyPrefill(data, request.form_data, { project, request }));
        } else {
          setFormData(buildInitialFormData(data, project, currentUser));
        }
      } catch (err) {
        console.error('Error loading schema:', err);
        if (!cancelled) setErrorMsg('Failed to load form');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [schemaKey, project, request, currentUser]);

  const visibleSections = useMemo(() => schema?.sections || [], [schema]);

  /**
   * Which facility the facility-scoped fields should draw from. The
   * compartment the client picked wins — that is the compartment being
   * adjusted, and its facility is what can actually supply the water. The
   * project's or request's facility is the fallback for forms with no
   * compartment picker, and for viewing a request later.
   */
  const facilityIdForFields = useMemo(() => {
    const fromAssignment = formData?.assignment?.facility_id;
    if (fromAssignment) return String(fromAssignment);
    const fromProject = project?.facility?._id || project?.facility;
    if (fromProject) return String(fromProject);
    const fromRequest = request?.facility?._id || request?.facility;
    return fromRequest ? String(fromRequest) : null;
  }, [formData?.assignment?.facility_id, project, request]);

  const projectForFields = useMemo(() => {
    if (project?._id) return project;
    const rp = request?.project_id;
    if (rp && typeof rp === 'object' && rp._id) return rp;
    if (rp) return { _id: rp };
    return null;
  }, [project, request]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg(null);

    const errs = validateAgainstSchema(schema, formData);
    if (errs.length) {
      setErrorMsg(errs.join('. '));
      return;
    }

    setSubmitting(true);
    try {
      let response;
      if (mode === 'create') {
        const piUser = project?.users?.find(u => (typeof u.role === 'object' ? u.role?.role : u.role) === 'PI');
        const piName = piUser ? `${piUser.first_name || piUser.user?.first_name || ''} ${piUser.last_name || piUser.user?.last_name || ''}`.trim() : '';
        response = await axios.post('/api/requests', {
          project_id: project._id,
          project_title: project.project_title,
          pi_name: piName || undefined,
          pi_email: piUser?.email || piUser?.user?.email,
          request_type_key: schemaKey,
          request_type: schema.label,
          form_data: formData
        });
      } else if (mode === 'edit') {
        response = await axios.put(`/api/requests/${request._id}`, {
          form_data: formData,
          comment
        });
      } else if (mode === 'admin_edit') {
        response = await axios.put(`/api/admin/requests/${request._id}`, {
          form_data: formData,
          comment
        });
      }

      toast.success(
        mode === 'create' ? 'Request submitted' :
        mode === 'edit' ? 'Request updated' :
        'Request edited'
      );
      if (onSubmitted) onSubmitted(response?.data);
    } catch (err) {
      console.error('Error submitting form:', err);
      const msg = err.response?.data?.errors?.join('. ')
        || err.response?.data?.message
        || err.response?.data?.error
        || 'Submission failed';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (!schema) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{errorMsg || 'Form unavailable'}</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>{schema.label}</Typography>
      {schema.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {schema.description}
        </Typography>
      )}
      <Divider sx={{ mb: 3 }} />

      {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

      <form onSubmit={handleSubmit}>
        {visibleSections.map((section, sIdx) => (
          <Box key={section.title || sIdx} sx={{ mb: 4 }}>
            {section.title && (
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                {section.title}
              </Typography>
            )}
            <Grid container spacing={2}>
              {section.fields.map((field) => {
                // Field types that need the whole row. The watering schedule
                // is the reason this list matters: at the default half width
                // its two-week grid and the inputs under it were squeezed to
                // the point where the labels were unreadable. The compartment
                // picker is here too — its options spell out the quantity,
                // assignment count and start date, which a half-width select
                // truncates.
                const fullWidth = [
                  'textarea',
                  'repeater',
                  'waterScheduleBuilder',
                  'chamberAssignmentSelect'
                ].includes(field.type);
                return (
                  <Grid
                    key={field.name}
                    item
                    xs={12}
                    md={fullWidth ? 12 : (field.colSpan || 6)}
                  >
                    <SchemaField
                      field={field}
                      formData={formData}
                      setFormData={setFormData}
                      disabled={submitting}
                      project={projectForFields}
                      facilityId={facilityIdForFields}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}

        {(mode === 'edit' || mode === 'admin_edit') && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Note about this change (optional)
            </Typography>
            <textarea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              style={{ width: '100%', padding: 8, fontFamily: 'inherit' }}
              placeholder="Why you are changing it — saved alongside the list of changed fields"
            />
          </Box>
        )}

        {/* Any turnaround note the request type carries, last thing before
            the client submits. */}
        {schema.notice && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {schema.notice}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          {onCancel && (
            <Button variant="outlined" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Saving...' : (
              mode === 'create' ? 'Submit Request' :
              mode === 'edit' ? 'Save Changes' :
              'Save Edit'
            )}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default DynamicRequestForm;
